import { useState } from 'react'
import { Megaphone, MailOpen } from '@/components/icons'
import { hapticLight } from '@/lib/haptics'
import { useQuery, useMutation } from '@pronteer/convex/react'
import { api } from '@pronteer/convex/api'
import { toast } from 'sonner'
import { useCurrentCrewMember } from '@/hooks/useCurrentCrewMember'
import { Card } from './ui/card'
import { SectionTitle } from './ui/section-title'
import { SwipeableRow } from './ui/swipeable-row'
import { AnnouncementCard } from './AnnouncementCard'
import { ViewAnnouncementDrawer } from './ViewAnnouncementDrawer'

export function HomeAnnouncements() {
  const { data: currentCrew } = useCurrentCrewMember()
  const [openRowId, setOpenRowId] = useState<string | null>(null)
  const [viewAnnouncementId, setViewAnnouncementId] = useState<string | null>(null)
  // Separate open flag; the id is kept on close so the view drawer can play
  // its exit animation (the next tap overwrites it).
  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false)

  const announcementsRaw = useQuery(
    api.announcements.list,
    currentCrew?.companyId && currentCrew?.id ? { companyId: currentCrew.companyId, crewId: currentCrew.id } : 'skip'
  )

  const markAsReadMutation = useMutation(api.announcements.markAsRead)

  const announcements = (announcementsRaw ?? []).filter((a) => a.published !== false)
  const filteredAnnouncements = announcements.filter((a) => !a.isReadByCurrentUser)

  const viewedAnnouncement =
    announcements.find((a) => a._id === viewAnnouncementId) ?? null

  const handleMarkAsRead = async (id: (typeof filteredAnnouncements)[0]['_id']) => {
    try {
      await markAsReadMutation({ id, crewId: currentCrew!.id })
      setOpenRowId(null)
    } catch (error) {
      console.error('Error marking as read:', error)
      toast.error('Failed to mark as read')
    }
  }

  const header = (
    <SectionTitle
      label="Announcements"
      count={filteredAnnouncements.length}
      icon={<Megaphone className="w-5 h-5 text-content-brand-bold" />}
    />
  )

  // No unread announcements — hide the section entirely
  if (filteredAnnouncements.length === 0) {
    return null
  }

  return (
    <div className="flex flex-col">
      {header}

      {/* Announcements Card */}
      <Card className="flex flex-col overflow-hidden px-3 py-0 divide-y divide-border-L1">
          {filteredAnnouncements.slice(0, 4).map((announcement) => {
            const rowId = `announcement-${announcement._id}`
            return (
              <SwipeableRow
                key={announcement._id}
                isOpen={openRowId === rowId}
                onOpenChange={(o) => setOpenRowId(o ? rowId : null)}
                onDelete={() => handleMarkAsRead(announcement._id)}
                actionLabel="Read"
                actionClassName="bg-content-info-bold"
                variant="icon"
                actionIcon={<MailOpen />}
              >
                <button
                  type="button"
                  onClick={() => {
                    hapticLight()
                    setViewAnnouncementId(announcement._id)
                    setIsViewDrawerOpen(true)
                  }}
                  className="block w-full text-left px-1 py-4"
                >
                  <AnnouncementCard
                    title={announcement.title}
                    message={announcement.message}
                    createdAt={announcement.createdAt}
                    isRead={announcement.isReadByCurrentUser}
                  />
                </button>
              </SwipeableRow>
            )
          })}
      </Card>

      <ViewAnnouncementDrawer
        open={isViewDrawerOpen}
        onOpenChange={setIsViewDrawerOpen}
        announcement={viewedAnnouncement}
      />
    </div>
  )
}
